import {Dialog} from '@reach/dialog';
import {DialogFooter, DialogTitle} from '../../../../../../components/Dialog';
import {
  FormGroup,
  FormItemGroup,
  RequireMarker,
} from '../../../../../../components/form-items';
import {Controller, useFormContext} from 'react-hook-form';
import Button, {ButtonGroup} from '../../../../../../components/Button';
import React from 'react';
import Select from '../../../../../../components/Select';
import {EmployeeSchedule, Shift} from '../../../../api/types';
import {DevTool} from '@hookform/devtools';

type ShiftFormProps = {
  isOpen?: boolean;
  onClose: () => void;
  shifts: Shift[];
  staffs: {id: string; name: string}[];
  units: {id: string; name: string}[];
  appliedUnitList: {value: string; label: string}[];
  onConfirm: (data: EmployeeSchedule) => void;
};

function ShiftBindingForm(props: ShiftFormProps) {
  const {isOpen, onClose, shifts, staffs, units, onConfirm, appliedUnitList} = props;
  const {control, register, watch, setValue, handleSubmit, errors, trigger, formState} =
    useFormContext<EmployeeSchedule>();
  const appliedUnit = watch('appliedUnit');

  return (
    <Dialog isOpen={isOpen} className={'!w-[500px]'} aria-label="shift-binding-dialog">
      <DialogTitle title={'排班員工設定'} onClose={onClose} />
      <form className={'company-form'} onSubmit={handleSubmit(onConfirm)}>
        <DevTool control={control}/>
        <input type={'hidden'} ref={register} name={'id'} />
        <div
          className={
            'flex flex-col border-b border-b-[#e9eced] pt-[18px] pb-10 px-3 mb-5'
          }
        >
          <FormGroup columnTemplates={1} data-test-id={'shift-id'}>
            <FormItemGroup>
              <label htmlFor={'shift'}>
                班別
                <RequireMarker />
              </label>
              <Controller
                render={({onChange, value}) => (
                  <Select
                    options={shifts?.map(s => ({
                      shiftId: s.id,
                      shiftName: s.storeName,
                    }))}
                    getOptionLabel={(o: any) => o.shiftName}
                    getOptionValue={(o: any) => o.shiftId}
                    onChange={(option: any) => onChange(option)}
                    value={value}
                    placeholder={'請選擇班別'}
                    noOptionsMessage={() => '請先建立班別'}
                    error={Boolean(errors.shift)}
                  />
                )}
                name="shift"
                control={control}
                rules={{required: true}}
                defaultValue={null}
              />
            </FormItemGroup>
            <FormItemGroup data-test-id={'applie-unit'}>
              <label htmlFor={'appliedUnit'}>
                適用對象(單位)
                <RequireMarker />
              </label>
              <Controller
                name={'appliedUnit'}
                render={({onChange, value}) => (
                  <Select
                    id={'appliedUnit'}
                    placeholder={'請選擇適用對象'}
                    options={appliedUnitList}
                    onChange={(option: {value: string}) => {
                      onChange(option.value);
                      setValue('units', []);
                      if (formState.isSubmitted) {
                        trigger('units');
                      }
                    }}
                    value={appliedUnitList.find(u => u.value === value)}
                    error={Boolean(errors.appliedUnit)}
                  />
                )}
                control={control}
                rules={{required: true}}
                defaultValue={null}
              />
            </FormItemGroup>
          </FormGroup>
          <FormGroup columnTemplates={1} data-test-id={'unit'}>
            <FormItemGroup>
              <label htmlFor={'units'}>
                適用對象
                <RequireMarker />
              </label>
              <Controller
                render={({onChange, value}) => (
                  <Select
                    isMulti
                    id={'units'}
                    placeholder={'請選擇適用對象'}
                    getOptionLabel={(o: any) => o.name}
                    getOptionValue={(o: any) => o.id}
                    options={
                      appliedUnit === 'Organization'
                        ? units || []
                        : appliedUnit === 'Employee'
                        ? staffs || []
                        : []
                    }
                    onChange={(option: any) => onChange(option)}
                    noOptionsMessage={() =>
                      appliedUnit ? '無選單' : '請先選擇適用對象(單位)'
                    }
                    value={value}
                    isDisabled={watch('appliedUnit') === 'Company'}
                    error={Boolean(errors.units)}
                  />
                )}
                name="units"
                control={control}
                rules={{
                  validate: {
                    isRequired: v => {
                      return (watch('appliedUnit') === 'Company' || v.length > 0) || 'required';
                    },
                  }
                }}
                defaultValue={[]}
              />
            </FormItemGroup>
          </FormGroup>
        </div>
        <DialogFooter>
          <ButtonGroup direction={'rtl'}>
            <Button type={'submit'} variant={'cta'} className={'confirm'}>
              確定
            </Button>
            <Button variant={'primary'} className={'close'} onClick={onClose}>
              取消
            </Button>
          </ButtonGroup>
        </DialogFooter>
      </form>
    </Dialog>
  );
}

export default ShiftBindingForm;
